import { EpisodeState } from "@/api/episodes-types";
import { useUser } from "@/queries/auth";
import { useEpisodeById } from "@/queries/episodes/use-episode-by-id";
import { useRejectStory } from "@/queries/stories/use-reject-story";
import { useAssetsReadyForValidation } from "@/queries/validation/use-assets-ready-for-validation";
import { useState } from "react";
import { useNavigate } from "react-router";

type UseRejectWholeEpisodeParams = {
  episodeId: string;
  shouldNavigateToNext?: boolean;
  onSuccess?: () => void;
};

export function useRejectWholeEpisode({
  episodeId,
  shouldNavigateToNext = false,
  onSuccess,
}: UseRejectWholeEpisodeParams) {
  const navigate = useNavigate();

  const { data: assetsForValidation } = useAssetsReadyForValidation();
  const { data: episodeData } = useEpisodeById(episodeId);
  const { data: user } = useUser();

  const [confirmRejectWholeEpisode, setConfirmRejectWholeEpisode] =
    useState(false);

  const canRejectEpisode =
    user?.id === episodeData?.visualsAssignee?.id &&
    episodeData?.state === EpisodeState.pending;

  const {
    mutate: rejectStory,
    isPending: isRejectingStory,
    error: rejectStoryError,
  } = useRejectStory();

  const handleRejectWholeEpisode = (notes?: string) => {
    rejectStory(
      { episodeId, rejectionNotes: notes },
      {
        onSuccess: () => {
          setConfirmRejectWholeEpisode(false);
          if (shouldNavigateToNext) {
            const nextAsset = assetsForValidation?.find(
              (asset) => asset.episodeId !== episodeId,
            );
            if (nextAsset) {
              if (nextAsset.type === "image") {
                navigate(`/images/${nextAsset.assetId}`);
              } else if (nextAsset.type === "mask") {
                navigate(`/masks/${nextAsset.assetId}`);
              }
            } else {
              navigate("/");
            }
          }
          onSuccess?.();
        },
        onError: () => {
          setConfirmRejectWholeEpisode(false);
        },
      },
    );
  };

  return {
    confirmRejectWholeEpisode,
    setConfirmRejectWholeEpisode,
    handleRejectWholeEpisode,
    isRejectingWholeEpisode: isRejectingStory,
    canRejectEpisode,
    error: rejectStoryError,
  };
}
